import { useState, useEffect } from "react";
import { FileText, Upload, Loader2, X, History, Check } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, API } from "@/lib/api";

const isPdf = (ct) => ct === "application/pdf";

export const ProductionFiles = ({ order, canEdit = true, showSuggestions = false }) => {
  const [files, setFiles] = useState(order.production_files || []);
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [sel, setSel] = useState(new Set());

  useEffect(() => {
    if (!showSuggestions || !order?.id) return;
    api.get(`/orders/${order.id}/suggested-production-files`).then((r) => setSuggestions(r.data?.suggestions || [])).catch(() => {});
  }, [showSuggestions, order?.id]);

  const addByIds = async (ids) => {
    if (!ids.length) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/production-files`, { file_ids: ids });
      setFiles(data.production_files);
      setSuggestions((prev) => prev.filter((s) => !ids.includes(s.id)));
      setSel(new Set());
      toast.success(`Dodano pliki produkcyjne: ${ids.length}`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const upload = async (list) => {
    const arr = Array.from(list || []);
    if (!arr.length) return;
    setBusy(true);
    const ids = [];
    for (const f of arr) {
      const fd = new FormData(); fd.append("file", f);
      try { const { data } = await api.post("/files", fd, { headers: { "Content-Type": "multipart/form-data" } }); ids.push(data.id); }
      catch (e) { toast.error(`${f.name}: ${errMsg(e)}`); }
    }
    if (ids.length) {
      try { const { data } = await api.post(`/orders/${order.id}/production-files`, { file_ids: ids }); setFiles(data.production_files); toast.success(`Dodano pliki produkcyjne: ${ids.length}`); }
      catch (e) { toast.error(errMsg(e)); }
    }
    setBusy(false);
  };

  const remove = async (fid) => {
    try { const { data } = await api.delete(`/orders/${order.id}/production-files/${fid}`); setFiles(data.production_files); toast.success("Usunięto plik produkcyjny"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid={`prod-files-${order.number}`}>
      <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Pliki produkcyjne</p>
      <div className="flex flex-wrap gap-2">
        {files.map((f) => (
          <div key={f.id} data-testid={`prod-file-${f.id}`} className="group relative flex items-center gap-2 rounded-md border border-border bg-secondary px-3 py-2 text-xs">
            <a href={`${API}/files/${f.id}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-amber-400 transition-colors">
              {isPdf(f.content_type) ? <FileText className="h-4 w-4 text-rose-400" /> : <img src={`${API}/files/${f.id}`} alt="" className="h-8 w-8 object-cover rounded" />}
              <span className="max-w-[160px] truncate">{f.filename}</span>
            </a>
            {canEdit && (
              <button type="button" data-testid={`prod-file-remove-${f.id}`} onClick={() => remove(f.id)} title="Usuń plik" className="ml-1 rounded p-0.5 text-muted-foreground hover:text-rose-400 hover:bg-rose-500/10 transition-colors">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}
        {canEdit && (
          <label data-testid={`prod-files-add-${order.number}`} className={`inline-flex items-center gap-2 rounded-md border border-dashed border-amber-400/50 px-3 py-2 text-xs font-bold text-amber-300 transition-colors ${busy ? "opacity-60" : "cursor-pointer hover:bg-amber-400/10"}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Dodaj plik
            <input data-testid={`prod-files-input-${order.number}`} type="file" multiple accept=".jpg,.jpeg,.png,.pdf" className="hidden" disabled={busy}
              onChange={(e) => { const f = Array.from(e.target.files || []); e.target.value = ""; upload(f); }} />
          </label>
        )}
        {!files.length && !canEdit && <span className="text-xs text-muted-foreground">Brak plików produkcyjnych.</span>}
      </div>
      {showSuggestions && canEdit && suggestions.length > 0 && (
        <div className="mt-3 rounded-md border border-dashed border-sky-400/40 bg-sky-400/5 p-3" data-testid={`prod-files-suggestions-${order.number}`}>
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-sky-300"><History className="h-3.5 w-3.5" />Z historii klienta ({suggestions.length})</p>
            <button type="button" data-testid={`prod-files-add-selected-${order.number}`} disabled={busy || !sel.size} onClick={() => addByIds([...sel])}
              className="inline-flex items-center gap-1 rounded-md bg-sky-500/20 px-2 py-1 text-[11px] font-bold text-sky-200 disabled:opacity-40 hover:bg-sky-500/30 transition-colors">
              <Check className="h-3 w-3" />Dodaj zaznaczone
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((s) => {
              const on = sel.has(s.id);
              return (
                <label key={s.id} data-testid={`prod-files-suggestion-${s.id}`} className={`flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs transition-colors ${on ? "border-sky-400 bg-sky-400/10" : "border-border bg-secondary hover:bg-secondary/70"}`}>
                  <input type="checkbox" checked={on} onChange={() => setSel((p) => { const n = new Set(p); n.has(s.id) ? n.delete(s.id) : n.add(s.id); return n; })} className="accent-sky-500" />
                  {isPdf(s.content_type) ? <FileText className="h-4 w-4 shrink-0 text-rose-400" /> : <img src={`${API}/files/${s.id}`} alt="" className="h-7 w-7 shrink-0 rounded object-cover" />}
                  <span className="max-w-[150px] truncate">{s.filename}</span>
                  <span className="shrink-0 text-[10px] text-muted-foreground">{s.order_number}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
